import { useEffect } from 'react';

/**
 * Keeps the screen on while `active`. The browser drops the lock whenever the
 * tab is hidden, so it is asked for again each time the tab comes back. Where
 * it is unsupported or refused, the screen follows the system's own timeout.
 */
export function useScreenWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) {
      return;
    }

    let sentinel: WakeLockSentinel | null = null;
    let stopped = false;

    async function acquire() {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (sentinel !== null && !sentinel.released) {
        return;
      }

      try {
        const next = await navigator.wakeLock.request('screen');
        if (stopped) {
          void next.release();
          return;
        }
        sentinel = next;
      } catch {
        /* Refused (battery saver, policy): the call goes on regardless. */
      }
    }

    function onVisibilityChange() {
      void acquire();
    }

    void acquire();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      void sentinel?.release();
    };
  }, [active]);
}
